import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  CronChangeLog,
  CronJob,
  CronListParams,
  CronSubmitPayload,
  CronMonitorResponse,
  CronMonitorFailedJobDetail,
  CronMonitorFailedJobsPage,
  CronMonitorHealthCheck,
  CronMonitorMissedJobsPage,
  CronMonitorQueueJobsPage,
  CronMonitorRunPage,
  CronMonitorSuccessfulJobsPage,
  CronMonitorTimeRange,
  FailedJob,
  QueuedJob,
} from '../types'

/* ---------------- Cron monitor ---------------- */

export async function getCronMonitor(
  limit = 3,
  timeRange: CronMonitorTimeRange = '24h',
): Promise<CronMonitorResponse> {
  try {
    const response = await apiClient.get<CronMonitorResponse>('/api/cron-monitor', {
      params: { limit, time_range: timeRange },
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load cron monitor')
  }
}

export async function getCronMonitorFailedJob(
  id: number,
): Promise<CronMonitorFailedJobDetail> {
  try {
    const response = await apiClient.get<CronMonitorFailedJobDetail>(
      `/api/cron-monitor/failed-job/${id}`,
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load full error')
  }
}

export async function getCronMonitorHealthCheck(): Promise<CronMonitorHealthCheck> {
  try {
    const response = await apiClient.get<CronMonitorHealthCheck>(
      '/api/cron-monitor/health-check',
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to run cron health check')
  }
}

export async function getCronMonitorRuns(
  page = 1,
  perPage = 15,
): Promise<CronMonitorRunPage> {
  try {
    const response = await apiClient.get<CronMonitorRunPage>('/api/cron-monitor/runs', {
      params: { page, per_page: perPage },
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load cron run history')
  }
}

export async function getCronMonitorFailedJobs(
  page = 1,
  perPage = 100,
  timeRange: CronMonitorTimeRange = '24h',
): Promise<CronMonitorFailedJobsPage> {
  try {
    const response = await apiClient.get<CronMonitorFailedJobsPage>(
      '/api/cron-monitor/failed-jobs',
      { params: { page, per_page: perPage, time_range: timeRange } },
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load failed jobs')
  }
}

export async function getCronMonitorMissedJobs(
  page = 1,
  perPage = 100,
): Promise<CronMonitorMissedJobsPage> {
  try {
    const response = await apiClient.get<CronMonitorMissedJobsPage>(
      '/api/cron-monitor/missed-jobs',
      { params: { page, per_page: perPage } },
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load missed jobs')
  }
}

export async function getCronMonitorStuckJobs(
  page = 1,
  perPage = 100,
): Promise<CronMonitorQueueJobsPage> {
  try {
    const response = await apiClient.get<CronMonitorQueueJobsPage>(
      '/api/cron-monitor/stuck-jobs',
      { params: { page, per_page: perPage } },
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load stuck jobs')
  }
}

export async function getCronMonitorWaitingJobs(
  page = 1,
  perPage = 100,
): Promise<CronMonitorQueueJobsPage> {
  try {
    const response = await apiClient.get<CronMonitorQueueJobsPage>(
      '/api/cron-monitor/waiting-jobs',
      { params: { page, per_page: perPage } },
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load waiting jobs')
  }
}

export async function getCronMonitorRunningJobs(
  page = 1,
  perPage = 100,
): Promise<CronMonitorQueueJobsPage> {
  try {
    const response = await apiClient.get<CronMonitorQueueJobsPage>(
      '/api/cron-monitor/running-jobs',
      { params: { page, per_page: perPage } },
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load running jobs')
  }
}

export async function getCronMonitorSuccessfulJobs(
  page = 1,
  perPage = 100,
  timeRange: CronMonitorTimeRange = '24h',
): Promise<CronMonitorSuccessfulJobsPage> {
  try {
    const response = await apiClient.get<CronMonitorSuccessfulJobsPage>(
      '/api/cron-monitor/successful-jobs',
      { params: { page, per_page: perPage, time_range: timeRange } },
    )
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load successful jobs')
  }
}

/* ---------------- Queued jobs ---------------- */

export async function getQueuedJobs(): Promise<QueuedJob[]> {
  try {
    const response = await apiClient.get<QueuedJob[]>('/api/queued_jobs')
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load queued jobs')
  }
}

function optionalQueuedPath(id: number | undefined): string {
  return id != null ? `/api/queued_jobs/${id}` : '/api/queued_jobs'
}

export async function deleteQueuedJob(id?: number): Promise<void> {
  try {
    await apiClient.delete(optionalQueuedPath(id))
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete queued job')
  }
}

export async function resetQueuedJob(id?: number): Promise<void> {
  try {
    await apiClient.put(optionalQueuedPath(id), {})
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to reset queued job')
  }
}

/* ---------------- Failed jobs ---------------- */

export async function getFailedJobs(): Promise<FailedJob[]> {
  try {
    const response = await apiClient.get<FailedJob[]>('/api/failed_jobs')
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load failed jobs')
  }
}

function optionalFailedPath(id: number | undefined): string {
  return id != null ? `/api/failed_jobs/${id}` : '/api/failed_jobs'
}

export async function deleteFailedJob(id?: number): Promise<void> {
  try {
    await apiClient.delete(optionalFailedPath(id))
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete failed job')
  }
}

export async function retryFailedJob(id?: number): Promise<void> {
  try {
    await apiClient.put(optionalFailedPath(id), {})
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to retry failed job')
  }
}

/* ---------------- Cron jobs ---------------- */

export async function getCronJobs(params: CronListParams = {}): Promise<CronJob[]> {
  try {
    const response = await apiClient.get<CronJob[]>('/api/cron', { params })
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load cron jobs')
  }
}

/**
 * GET /api/cron/{id} — change-log entries for a single cron job. Mirrors
 * Angular `CronInfoUpdateComponent.getCronDetail()`.
 */
export async function getCronDetail(cronId: string | number): Promise<CronChangeLog[]> {
  try {
    const response = await apiClient.get<CronChangeLog[]>(`/api/cron/${cronId}`)
    return response.data ?? []
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load cron change log')
  }
}

export async function saveCronJob(
  payload: CronSubmitPayload,
  cronId?: string | number,
): Promise<CronJob> {
  try {
    const url = cronId ? `/api/cron/${cronId}` : '/api/cron'
    const response = await apiClient.post<CronJob>(url, payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save cron job')
  }
}

export async function toggleCronStatus(cronJob: CronJob): Promise<CronJob> {
  try {
    const nextStatus = cronJob.cron_status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'
    const response = await apiClient.post<CronJob>(`/api/cron/${cronJob.cron_id}`, {
      status: nextStatus,
      description: cronJob.cron_description,
    })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to update cron status')
  }
}
